import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getActiveRole } from "../../../utils/auth";
import { fetchBranches } from "../../../api/branches";
import { listLevels } from "../../../api/levels";
import { formatLevelLabel } from "../../../utils/formatLevelLabel";
import { listBatches } from "../../../api/batches";
import { listCoaches } from "../../../api/users";
import API from "../../../api";
import {
    fetchStudentAttendanceSummary,
    exportStudentAttendanceSummary,
    emailStudentAttendanceSummary,
} from "../../../api/adminReports";
import DatePicker from "../../../components/common/DatePicker";
import ReportExportBar from "../../../components/reports/ReportExportBar";
import ExcelDownloadButton from "../../../components/common/ExcelDownloadButton";
import { exportToExcel } from "../../../utils/excelExport";
import ReportGenerateButton from "../../../components/reports/ReportGenerateButton";
import UserLink from "../../../components/common/UserLink";
import { SelectInput } from "../../../components/common/form";
import PaginationFooter from "../../../components/common/PaginationFooter";
import SortableTableHeader from "../../../components/common/SortableTableHeader";
import useSortedClientPagination from "../../../hooks/useSortedClientPagination";
import { studentAttendanceSummarySortColumns } from "../../../config/tableSortColumns";
import {
    ChartPanel,
    CompareBar,
    CompositionDonut,
    CHART_COLORS,
} from "../../../components/charts";
import { localizeBatchName } from '../../../utils/displayNames';
import {
    firstDayOfMonthRegional,
    todayRegionalDate,
    toISODateFromRegional,
} from "../../../utils/date";
import { apiErrorMessage } from '../../../utils/apiError';

function getFirstDayOfMonth() {
    return firstDayOfMonthRegional();
}
function getToday() {
    return todayRegionalDate();
}
function ddmmyyyyToIso(str) {
    const iso = toISODateFromRegional(str);
    return iso || null;
}

// ─── Download helper ─────────────────────────────────────────────────────────
async function triggerDownload(blobReq, filename) {
    const res = await blobReq;
    const blob = new Blob([res.data], {
        type: res.headers["content-type"] || "application/octet-stream",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

// ─── Attendance bar ──────────────────────────────────────────────────────────
function AttendanceBar({ pct }) {
    const colour =
        pct >= 75 ? "bg-green-500" : pct >= 50 ? "bg-yellow-400" : "bg-red-500";
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                <div className={`h-2 rounded-full ${colour}`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            <span
                className={`text-xs font-semibold w-10 text-right ${pct >= 75 ? "text-green-700" : pct >= 50 ? "text-yellow-700" : "text-red-700"
                    }`}
            >
                {pct.toFixed(1)}%
            </span>
        </div>
    );
}

export default function StudentAttendanceSummaryReport() {
    const { t } = useTranslation();
    const apiRole = getActiveRole() || "ADMIN";
    const role = apiRole.toLowerCase();
    const isCoach = apiRole === "COACH";

    const [from, setFrom] = useState(getFirstDayOfMonth());
    const [to, setTo] = useState(getToday());
    const [branchId, setBranchId] = useState("");
    const [levelId, setLevelId] = useState("");
    const [batchId, setBatchId] = useState("");
    const [coachId, setCoachId] = useState("");

    const [branches, setBranches] = useState([]);
    const [levels, setLevels] = useState([]);
    const [batches, setBatches] = useState([]);
    const [coaches, setCoaches] = useState([]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [hasRun, setHasRun] = useState(false);
    const [filtersLoadError, setFiltersLoadError] = useState(false);
    const [batchesLoadError, setBatchesLoadError] = useState(false);

    const reportRows = data?.rows ?? [];
    const rowsPagination = useSortedClientPagination(reportRows, {
        columns: studentAttendanceSummarySortColumns,
        resetKey: `${from}-${to}-${branchId}-${levelId}-${batchId}-${coachId}-${reportRows.length}`,
    });

    // ── Load filter options ─────────────────────────────────────────────────
    useEffect(() => {
        let failed = false;
        const tasks = [];
        if (!isCoach) {
            const branchLoader = apiRole === "MANAGER"
                ? API.get("/manager/branches").then((res) => setBranches(res?.data || []))
                : fetchBranches().then((res) => setBranches(res?.data || []));
            tasks.push(branchLoader.catch(() => { setBranches([]); failed = true; }));

            const coachLoader = apiRole === "MANAGER"
                ? API.get("/manager/users", { params: { role: "COACH", page: 0, size: 500 } }).then((res) => {
                    const data = res?.data;
                    setCoaches(Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : []));
                })
                : listCoaches().then((list) => setCoaches(list || []));
            tasks.push(coachLoader.catch(() => { setCoaches([]); failed = true; }));
        }
        tasks.push(listLevels(apiRole).then((res) => setLevels(res.data?.content || [])).catch(() => { setLevels([]); failed = true; }));
        Promise.all(tasks).finally(() => setFiltersLoadError(failed));
    }, [apiRole, isCoach]);

    // Cascade batches by level
    useEffect(() => {
        let alive = true;

        setBatches([]);
        setBatchId("");

        if (!levelId) {
            return () => { alive = false; };
        }

        setBatchesLoadError(false);
        listBatches(apiRole, { page: 0, size: 500, levelId: Number(levelId) })
            .then((res) => {
                if (!alive) return;
                const vals = res?.data?.content ?? res?.data ?? [];
                setBatches(vals);
                setBatchesLoadError(false);
            })
            .catch(() => {
                if (alive) {
                    setBatches([]);
                    setBatchesLoadError(true);
                }
            });

        return () => { alive = false; };
    }, [apiRole, levelId]);

    const generate = async () => {
        const fromIso = ddmmyyyyToIso(from);
        const toIso = ddmmyyyyToIso(to);
        if (!fromIso || !toIso) {
            setErr(t('reports.common.invalidDateRange'));
            return;
        }
        setErr("");
        setLoading(true);
        setHasRun(true);
        try {
            const res = await fetchStudentAttendanceSummary({
                role,
                from: fromIso,
                to: toIso,
                branchId: branchId || undefined,
                levelId: levelId || undefined,
                batchId: batchId || undefined,
                coachId: coachId || undefined,
            });
            setData(res.data);
        } catch (e) {
            setErr(apiErrorMessage(e, t('reports.common.failedToLoad')));
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    // ── Export helpers ───────────────────────────────────────────────────────
    const fromIso = ddmmyyyyToIso(from);
    const toIso = ddmmyyyyToIso(to);
    const exportParams = {
        role,
        from: fromIso,
        to: toIso,
        branchId: branchId || undefined,
        levelId: levelId || undefined,
        batchId: batchId || undefined,
        coachId: coachId || undefined,
    };

    const handleExportPdf = () =>
        triggerDownload(
            exportStudentAttendanceSummary({ ...exportParams, format: "pdf" }),
            "student-attendance-summary.pdf"
        );

    const handleExportCsv = () =>
        triggerDownload(
            exportStudentAttendanceSummary({ ...exportParams, format: "csv" }),
            "student-attendance-summary.csv"
        );

    const handleSendEmail = (email) =>
        emailStudentAttendanceSummary({ ...exportParams, format: "pdf", toEmail: email });

    const s = data?.summary;

    const attendanceBucketData = useMemo(() => {
        const buckets = [
            { name: "0–50%", value: 0, color: CHART_COLORS.danger },
            { name: "50–70%", value: 0, color: CHART_COLORS.warning },
            { name: "70–85%", value: 0, color: CHART_COLORS.accent },
            { name: "85–100%", value: 0, color: CHART_COLORS.success },
        ];
        for (const row of reportRows) {
            const pct = Number(row.attendancePercent);
            if (Number.isNaN(pct)) continue;
            if (pct < 50) buckets[0].value += 1;
            else if (pct < 70) buckets[1].value += 1;
            else if (pct < 85) buckets[2].value += 1;
            else buckets[3].value += 1;
        }
        return buckets;
    }, [reportRows]);

    const presentAbsentData = useMemo(() => {
        if (!s) return [];
        return [
            {
                name: t("reports.common.present", { defaultValue: "Present" }),
                value: Number(s.totalPresentSlots) || 0,
                color: CHART_COLORS.success,
            },
            {
                name: t("reports.common.absent", { defaultValue: "Absent" }),
                value: Number(s.totalAbsentSlots) || 0,
                color: CHART_COLORS.danger,
            },
        ].filter((d) => d.value > 0);
    }, [s, t]);

    return (
        <div className="p-6 space-y-6">
            <p className="text-sm text-gray-600">
                {isCoach
                    ? t('reports.studentAttendance.subtitleCoach')
                    : t('reports.studentAttendance.subtitleAdmin')}
            </p>

            {/* ── Filters ────────────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    {t('reports.common.filters')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-gray-700 mb-2 block">📅 {t('reports.common.fromDate')}</label>
                        <DatePicker value={from} onChange={setFrom} className="w-full" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-700 mb-2 block">📅 {t('reports.common.toDate')}</label>
                        <DatePicker value={to} onChange={setTo} className="w-full" />
                    </div>
                    {!isCoach && (
                    <SelectInput
                        label={t('reports.common.filterByBranch')}
                        value={branchId}
                        onChange={(e) => setBranchId(e.target.value)}
                    >
                        <option value="">{t('reports.common.allBranches')}</option>
                        {branches.map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                    </SelectInput>
                    )}
                    <SelectInput
                        label={t('reports.common.filterByLevel')}
                        value={levelId}
                        onChange={(e) => { setLevelId(e.target.value); setBatchId(""); }}
                    >
                        <option value="">{t('reports.common.allLevels')}</option>
                        {levels.map((l) => (
                            <option key={l.levelId} value={l.levelId}>{formatLevelLabel(l)}</option>
                        ))}
                    </SelectInput>
                    <SelectInput
                        label={t('reports.common.filterByBatch')}
                        value={batchId}
                        onChange={(e) => setBatchId(e.target.value)}
                        disabled={!levelId}
                        title={!levelId ? t('reports.common.selectLevelFirst', { defaultValue: 'Select a level first to filter by batch' }) : undefined}
                        note={!levelId ? t('reports.common.selectLevelFirst', { defaultValue: 'Select a level first to filter by batch' }) : undefined}
                    >
                        <option value="">{t('reports.common.allBatches')}</option>
                        {batches.map((b) => {
                            const id = b.batchId ?? b.id;
                            return (
                            <option key={id} value={id}>{b.name}</option>
                            );
                        })}
                    </SelectInput>
                    {!isCoach && (
                    <SelectInput
                        label={t('reports.common.filterByCoach')}
                        value={coachId}
                        onChange={(e) => setCoachId(e.target.value)}
                    >
                        <option value="">{t('reports.common.allCoaches')}</option>
                        {coaches.map((c) => {
                            const name = [c.firstName, c.middleName, c.lastName].filter(Boolean).join(" ") || `#${c.id}`;
                            return <option key={c.id} value={c.id}>{name}</option>;
                        })}
                    </SelectInput>
                    )}
                    <div className="sm:col-span-2 xl:col-span-1 flex items-end">
                        <ReportGenerateButton
                            onClick={generate}
                            loading={loading}
                            label={t('reports.common.search')}
                            icon="search"
                        />
                    </div>
                </div>
            </div>

            {(filtersLoadError || batchesLoadError) && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="status">{t('common.optionsLoadFailed')}</div>
            )}
            {err && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{err}</div>
            )}

            {hasRun && !loading && reportRows.length === 0 && presentAbsentData.length === 0 ? (
                <ChartPanel
                    title={t("charts.attendanceBuckets", { defaultValue: "Attendance % distribution" })}
                    empty
                    emptyMessage={t("charts.noAttendanceBucketData", { defaultValue: "No attendance data to chart for this period" })}
                />
            ) : hasRun ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ChartPanel
                        title={t("charts.attendanceBuckets", { defaultValue: "Attendance % distribution" })}
                        subtitle={t("charts.attendanceBucketsHint", { defaultValue: "Students by attendance rate bucket" })}
                        loading={loading}
                        empty={!loading && reportRows.length === 0}
                        emptyMessage={t("charts.noAttendanceBucketData", { defaultValue: "No student rows to chart" })}
                        error={err || null}
                        height={240}
                    >
                        <CompareBar
                            data={attendanceBucketData}
                            height={220}
                            series={[{ dataKey: "value", name: t("reports.common.students", { defaultValue: "Students" }) }]}
                        />
                    </ChartPanel>
                    <ChartPanel
                        title={t("charts.presentVsAbsent", { defaultValue: "Present vs absent slots" })}
                        subtitle={t("charts.presentVsAbsentHint", { defaultValue: "Total attendance slots in period" })}
                        loading={loading}
                        empty={!loading && presentAbsentData.length === 0}
                        emptyMessage={t("charts.noSlotData", { defaultValue: "No attendance slots to chart" })}
                        error={err || null}
                        height={240}
                    >
                        <CompositionDonut data={presentAbsentData} chartSize={180} />
                    </ChartPanel>
                </div>
            ) : null}

            {/* ── Summary Cards ────────────────────────────────────────────────── */}
            {!loading && s && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-md p-5 text-white">
                        <div className="text-xs opacity-90 mb-1">{t('reports.studentAttendance.totalStudents')}</div>
                        <div className="text-3xl font-bold">{s.totalStudents}</div>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg shadow-md p-5 text-white">
                        <div className="text-xs opacity-90 mb-1">{t('reports.studentAttendance.avgAttendance')}</div>
                        <div className="text-3xl font-bold">{s.averageAttendancePercent.toFixed(1)}%</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-5 text-white">
                        <div className="text-xs opacity-90 mb-1">{t('reports.studentAttendance.totalPresent')}</div>
                        <div className="text-3xl font-bold">{s.totalPresentSlots}</div>
                    </div>
                    <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-md p-5 text-white">
                        <div className="text-xs opacity-90 mb-1">{t('reports.studentAttendance.totalAbsent')}</div>
                        <div className="text-3xl font-bold">{s.totalAbsentSlots}</div>
                    </div>
                </div>
            )}

            {/* ── Data Table ───────────────────────────────────────────────────── */}
            {!loading && data?.rows?.length > 0 && (
                <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gray-50">
                        <h3 className="text-base font-semibold text-gray-800">
                            👨‍🎓 {t('reports.studentAttendance.detailsTitle')}
                            <span className="ml-2 text-sm font-normal text-gray-500">({t('reports.common.records', { count: data.rows.length })})</span>
                        </h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-700 text-white">
                                    <SortableTableHeader label={t('reports.common.student')} columnKey="student" sortKey={rowsPagination.sortKey} sortDir={rowsPagination.sortDir} onSort={rowsPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <th className="px-4 py-3 text-center font-semibold">{t('reports.common.batch')}</th>
                                    <th className="px-4 py-3 text-center font-semibold">{t('reports.common.level')}</th>
                                    <SortableTableHeader label={t('reports.common.branch')} columnKey="branch" sortKey={rowsPagination.sortKey} sortDir={rowsPagination.sortDir} onSort={rowsPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <th className="px-4 py-3 text-center font-semibold">{t('reports.common.coach')}</th>
                                    <SortableTableHeader label={t('reports.common.sessions')} columnKey="sessions" sortKey={rowsPagination.sortKey} sortDir={rowsPagination.sortDir} onSort={rowsPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <th className="px-4 py-3 text-center font-semibold">{t('reports.common.present')}</th>
                                    <th className="px-4 py-3 text-center font-semibold">{t('reports.common.absent')}</th>
                                    <SortableTableHeader label={t('reports.common.attendancePct')} columnKey="rate" sortKey={rowsPagination.sortKey} sortDir={rowsPagination.sortDir} onSort={rowsPagination.toggleSort} className="px-4 py-3 text-center font-semibold min-w-[140px]" align="center" />
                                </tr>
                            </thead>
                            <tbody>
                                {rowsPagination.pageItems.map((row, i) => {
                                    const isLow = row.attendancePercent < 50;
                                    return (
                                        <tr
                                            key={`${row.studentId}-${localizeBatchName(row.batchName, t)}-${i}`}
                                            className={`${isLow
                                                ? "bg-red-50"
                                                : i % 2 === 0
                                                    ? "bg-white"
                                                    : "bg-gray-50"
                                                } hover:bg-gray-100 transition-colors`}
                                        >
                                            <td className="px-4 py-3 text-center font-medium text-gray-800">
                                                {row.studentId ? (
                                                    <UserLink id={row.studentId} name={row.studentName} />
                                                ) : (
                                                    row.studentName
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center text-gray-700">{localizeBatchName(row.batchName, t)}</td>
                                            <td className="px-4 py-3 text-center text-gray-600">{row.levelName}</td>
                                            <td className="px-4 py-3 text-center text-gray-600">{row.branchName}</td>
                                            <td className="px-4 py-3 text-center text-gray-600">{row.coachName}</td>
                                            <td className="px-4 py-3 text-center text-gray-700">{row.totalSessions}</td>
                                            <td className="px-4 py-3 text-center text-green-700 font-medium">{row.presentCount}</td>
                                            <td className="px-4 py-3 text-center text-red-700 font-medium">{row.absentCount}</td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="mx-auto max-w-[160px]">
                                                    <AttendanceBar pct={row.attendancePercent} />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {rowsPagination.total > 0 && (
                        <PaginationFooter
                            page={rowsPagination.page}
                            size={rowsPagination.size}
                            total={rowsPagination.total}
                            onPageChange={rowsPagination.setPage}
                            onSizeChange={rowsPagination.setSize}
                        />
                    )}
                    {/* Low-attendance legend */}
                    <div className="px-6 py-3 bg-gray-50 border-t text-xs text-gray-500 flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-200" />
                        {t('reports.studentAttendance.lowAttendanceLegend')}
                    </div>
                </div>
            )}

            {/* ── Empty state ──────────────────────────────────────────────────── */}
            {!loading && hasRun && data && !data.rows?.length && (
                <div className="text-center py-16 text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="font-medium">{t('reports.studentAttendance.empty')}</p>
                </div>
            )}

            {/* ── Export Bar ───────────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center gap-3">
                <ExcelDownloadButton
                    onExport={() => exportToExcel([
                        { header: t('reports.common.student'), key: 'studentName', width: 24 },
                        { header: t('reports.common.batch'), key: 'batchName', render: (v) => localizeBatchName(v, t), width: 20 },
                        { header: t('reports.common.level'), key: 'levelName', width: 18 },
                        { header: t('reports.common.branch'), key: 'branchName', width: 18 },
                        { header: t('reports.common.coach'), key: 'coachName', width: 20 },
                        { header: t('reports.common.sessions'), key: 'totalSessions', width: 10 },
                        { header: t('reports.common.present'), key: 'presentCount', width: 10 },
                        { header: t('reports.common.absent'), key: 'absentCount', width: 10 },
                        { header: t('reports.common.attendancePct'), key: 'attendancePercent', render: (v) => Number(v || 0).toFixed(1), width: 12 },
                    ], data?.rows || [], 'student-attendance-summary')}
                    filename="student-attendance-summary"
                    disabled={!data?.rows?.length}
                />
                <ReportExportBar
                    disabled={!data}
                    onExportPdf={handleExportPdf}
                    onExportCsv={handleExportCsv}
                    onSendEmail={handleSendEmail}
                    emailLabel={t('reports.common.enterEmail')}
                />
            </div>
        </div>
    );
}
